import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Flame,
  Brain,
  Heart,
  Moon,
  Hash,
  CheckCircle2,
  AlertTriangle,
  MapPin,
  BookHeart,
  PauseCircle,
  ArrowUpRight,
  Quote,
  Target,
  Compass,
} from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { track } from "@/lib/analytics";
import ClarityCard from "@/components/ClarityCard";
import PsychRadar from "@/components/PsychRadar";
import Sparks from "@/components/Sparks";
import { useLang } from "@/lib/i18n";

/* ---------------- helpers ---------------- */
const LEVEL_TONE = {
  Poor: { color: "#f87171" },
  Moderate: { color: "#FBE38E" },
  High: { color: "#34d399" },
  Excellent: { color: "#5EEAD4" },
};
function normalizeLevel(v) {
  if (!v) return "Moderate";
  const s = String(v).trim();
  if (LEVEL_TONE[s]) return s;
  const low = s.toLowerCase();
  if (low.startsWith("poor") || low.startsWith("low")) return "Poor";
  if (low.startsWith("excel") || low.startsWith("very high")) return "Excellent";
  if (low.startsWith("high") || low.startsWith("strong") || low.startsWith("good")) return "High";
  return "Moderate";
}
function toPct(v) {
  if (typeof v === "number" && Number.isFinite(v)) return Math.max(0, Math.min(100, Math.round(v)));
  if (typeof v === "string") {
    const n = parseInt(v.replace("%", ""), 10);
    if (!Number.isNaN(n)) return Math.max(0, Math.min(100, n));
    const map = { Poor: 40, Moderate: 65, High: 82, Excellent: 92 };
    if (map[v]) return map[v];
  }
  return null;
}

/* ---------------- primitives ---------------- */
function Section({ kicker, title, children, testId, className = "" }) {
  return (
    <section className={`mt-12 md:mt-14 ${className}`} data-testid={testId}>
      {kicker && (
        <div className="text-[10px] tracking-[0.28em] uppercase gold-text mb-3">
          {kicker}
        </div>
      )}
      {title && (
        <h2 className="font-serif text-3xl md:text-4xl font-light text-white mb-7 leading-tight">
          {title}
        </h2>
      )}
      {children}
    </section>
  );
}
function Tile({ icon: Icon, label, children, testId, className = "" }) {
  return (
    <div className={`glass rounded-3xl p-7 md:p-8 ${className}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="w-4 h-4 gold-text" strokeWidth={1.5} />}
        <div className="text-[10px] tracking-[0.25em] uppercase text-slate-400">
          {label}
        </div>
      </div>
      {children}
    </div>
  );
}

/* Structured 4-line therapy block used by mind + emotions. Falls back to a
 * legacy free-text paragraph if the LLM returned a string instead of a dict. */
function TherapyBlock({ data, testId, t }) {
  if (typeof data === "string" || !data) {
    return (
      <p className="report-body" data-testid={`${testId}-text`}>
        {data || <span className="text-slate-500 italic">{t("res.notAvailable")}</span>}
      </p>
    );
  }
  const rows = [
    { k: t("res.core_pattern"), v: data.core_pattern },
    { k: t("res.strength"),     v: data.strength },
    { k: t("res.hidden_tension"), v: data.hidden_tension },
    { k: t("res.meaning"),      v: data.meaning },
  ];
  return (
    <div className="space-y-4" data-testid={`${testId}-therapy`}>
      {rows.map((row) =>
        row.v ? (
          <div key={row.k} className="flex flex-col sm:flex-row sm:gap-6">
            <div className="text-[10px] tracking-[0.22em] uppercase gold-text sm:w-40 mb-1 sm:mb-0 sm:pt-1 shrink-0">
              {row.k}
            </div>
            <p className="report-body flex-1">{row.v}</p>
          </div>
        ) : null,
      )}
    </div>
  );
}

/* Structured astrology 3-line block. */
function AstroBlock({ data, testId, t }) {
  if (typeof data === "string" || !data) {
    return (
      <p className="report-body" data-testid={`${testId}-text`}>
        {data || <span className="text-slate-500 italic">{t("res.notAvailable")}</span>}
      </p>
    );
  }
  const rows = [
    { k: t("res.current_timing"),   v: data.current_timing },
    { k: t("res.natural_tendency"), v: data.natural_tendency },
    { k: t("res.current_lesson"),   v: data.current_lesson },
  ];
  return (
    <div className="space-y-4" data-testid={`${testId}-astro`}>
      {rows.map((row) =>
        row.v ? (
          <div key={row.k} className="flex flex-col sm:flex-row sm:gap-6">
            <div className="text-[10px] tracking-[0.22em] uppercase gold-text sm:w-40 mb-1 sm:mb-0 sm:pt-1 shrink-0">
              {row.k}
            </div>
            <p className="report-body flex-1">{row.v}</p>
          </div>
        ) : null,
      )}
    </div>
  );
}

/* Structured numerology block — 6 labelled number cards (Life Path → Freedom,
 * Expression → Wisdom, etc.). */
const NUM_ORDER = ["life_path", "expression", "soul", "personality", "personal_year", "pinnacle"];
const NUM_LABELS = {
  life_path:     { label: "Freedom",        title: "Life Path" },
  expression:    { label: "Wisdom",         title: "Expression" },
  soul:          { label: "Experience",     title: "Soul" },
  personality:   { label: "Expression",     title: "Personality" },
  personal_year: { label: "Transformation", title: "Personal Year" },
  pinnacle:      { label: "Growth Phase",   title: "Pinnacle" },
};
function NumerologyGrid({ numerology, structured }) {
  // Prefer the LLM's structured object (each key has {number,label,meaning}).
  // Fall back to computed numerology numbers with the fixed labels.
  const items = NUM_ORDER.map((k) => {
    const s = structured?.[k];
    const rawFallback =
      k === "life_path" ? numerology.life_path :
      k === "expression" ? numerology.destiny :
      k === "soul" ? numerology.soul :
      k === "personality" ? numerology.personality :
      k === "personal_year" ? numerology.personal_year :
      k === "pinnacle" ? numerology?.pinnacle?.number :
      null;
    const number = s?.number ?? rawFallback;
    if (number == null || number === 0) return null;
    const meta = NUM_LABELS[k];
    return {
      key: k,
      title: meta.title,
      label: s?.label || meta.label,
      number,
      meaning: s?.meaning || "",
    };
  }).filter(Boolean);

  if (!items.length) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="numerology-grid">
      {items.map((it) => (
        <div
          key={it.key}
          className="rounded-2xl p-4"
          style={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <div className="flex items-baseline justify-between mb-1">
            <div className="text-[9px] tracking-[0.22em] uppercase text-slate-400">
              {it.title}
            </div>
            <div className="text-[9px] tracking-[0.22em] uppercase gold-text">
              {it.label}
            </div>
          </div>
          <div className="font-serif text-3xl text-white leading-none mt-1 mb-2">
            {it.number}
          </div>
          {it.meaning && (
            <p className="text-xs text-slate-300 leading-relaxed">
              {it.meaning}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

/* Highlighted blind-spot warning card — one of the most emotionally
 * impactful sections. */
function BlindSpotCard({ data, t }) {
  if (!data) return null;
  const rows =
    typeof data === "string"
      ? [{ k: t("res.deeper_truth"), v: data }]
      : [
          { k: t("res.current_belief"),  v: data.current_belief },
          { k: t("res.deeper_truth"),    v: data.deeper_truth },
          { k: t("res.why_it_matters"),  v: data.why_it_matters },
        ];
  return (
    <div
      className="rounded-3xl p-8 md:p-10"
      style={{
        background:
          "linear-gradient(140deg, rgba(251,163,26,0.10) 0%, rgba(180,60,60,0.08) 100%)",
        border: "1px solid rgba(251,163,26,0.35)",
        boxShadow: "0 24px 60px rgba(0,0,0,0.4), 0 0 32px rgba(251,163,26,0.10)",
      }}
      data-testid="results-blind-spot"
    >
      <div className="flex items-center gap-3 mb-6">
        <AlertTriangle className="w-5 h-5" style={{ color: "#FBA31A" }} strokeWidth={1.5} />
        <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: "#FBA31A" }}>
          {t("res.blindSpot")}
        </div>
      </div>
      <div className="space-y-5">
        {rows.map((row) =>
          row.v ? (
            <div key={row.k}>
              <div className="text-[10px] tracking-[0.22em] uppercase text-slate-400 mb-1.5">
                {row.k}
              </div>
              <p className="report-body">{row.v}</p>
            </div>
          ) : null,
        )}
      </div>
    </div>
  );
}

/* Confidence bar — percentage over an animated gold rail. */
function ConfidenceBar({ label, value }) {
  const pct = toPct(value);
  return (
    <div className="glass rounded-2xl p-5" data-testid={`confidence-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-[10px] tracking-[0.22em] uppercase text-slate-400">{label}</span>
        <span className="font-serif text-xl gold-text tabular-nums">
          {pct == null ? "—" : `${pct}%`}
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: pct == null ? 0 : `${pct}%` }}
          transition={{ duration: 1.1, ease: "easeOut" }}
          className="h-full rounded-full"
          style={{
            background: "linear-gradient(90deg, #FBE38E 0%, #D4AF37 100%)",
            boxShadow: "0 0 12px rgba(212,175,55,0.35)",
          }}
        />
      </div>
    </div>
  );
}

/* ---------------- extra i18n strings (inline) ----------------
 * Small dictionary of Results-only labels. Kept here so we don't have to
 * plumb 20+ new keys through i18n.js for this iteration.
 */
const R_STR = {
  en: {
    "res.core_pattern": "Core Pattern",
    "res.strength": "Strength",
    "res.hidden_tension": "Hidden Tension",
    "res.meaning": "Meaning",
    "res.current_timing": "Current Timing",
    "res.natural_tendency": "Your Natural Tendency",
    "res.current_lesson": "Current Lesson",
    "res.current_belief": "Current belief",
    "res.deeper_truth": "Possible deeper truth",
    "res.why_it_matters": "Why this matters",
    "res.oneLine": "If we had to summarize in one sentence",
    "res.archetype": "Your Archetype",
    "res.profileTitle": "Your decision profile",
    "res.profileSubtitle": "How your five psychological axes are currently balanced.",
    "res.psychConfidence": "Psychological confidence",
    "res.astroAlignment": "Astrological alignment",
    "res.numConsistency": "Numerological consistency",
    "res.overallClarity": "Overall clarity",
    "res.hero.eyebrow": "Your unified clarity reading",
    "res.hero.subline": "We blended your decision profile, astrological context and numerological signatures into one report — reflective, not predictive.",
    "res.finalReflection": "A final reflection",
    "res.spiritual.section": "A practice for the road",
    "res.spiritual.title": "Spiritual practice.",
    "res.newReading": "New reading",
    "res.disclaimer": "Mantrana offers decision intelligence informed by psychology, astrology and numerology. It is reflective, not predictive — a mirror, not a verdict.",
    "res.confidence.section": "How confident is this read",
    "res.confidence.title": "Confidence indicators",
  },
  hi: {
    "res.core_pattern": "मूल पैटर्न",
    "res.strength": "शक्ति",
    "res.hidden_tension": "छिपा हुआ तनाव",
    "res.meaning": "अर्थ",
    "res.current_timing": "वर्तमान समय",
    "res.natural_tendency": "आपकी स्वाभाविक प्रवृत्ति",
    "res.current_lesson": "वर्तमान सीख",
    "res.current_belief": "वर्तमान विश्वास",
    "res.deeper_truth": "संभावित गहरा सत्य",
    "res.why_it_matters": "यह क्यों महत्वपूर्ण है",
    "res.oneLine": "यदि एक वाक्य में संक्षेप करना हो",
    "res.archetype": "आपका मूल स्वरूप",
    "res.profileTitle": "आपकी निर्णय प्रोफ़ाइल",
    "res.profileSubtitle": "आपके पाँच मनोवैज्ञानिक आयाम अभी किस संतुलन में हैं।",
    "res.psychConfidence": "मनोवैज्ञानिक विश्वास",
    "res.astroAlignment": "ज्योतिषीय संरेखण",
    "res.numConsistency": "अंकशास्त्रीय संगति",
    "res.overallClarity": "समग्र स्पष्टता",
    "res.hero.eyebrow": "आपका एकीकृत स्पष्टता पठन",
    "res.hero.subline": "हमने आपकी निर्णय प्रोफ़ाइल, ज्योतिषीय संदर्भ और अंकशास्त्रीय हस्ताक्षरों को एक रिपोर्ट में पिरोया है — चिंतनशील, न कि भविष्यसूचक।",
    "res.finalReflection": "एक अंतिम चिंतन",
    "res.spiritual.section": "मार्ग के लिए एक अभ्यास",
    "res.spiritual.title": "आध्यात्मिक अभ्यास।",
    "res.newReading": "नया पठन",
    "res.disclaimer": "मंत्रणा मनोविज्ञान, ज्योतिष और अंकशास्त्र से प्रेरित निर्णय-बुद्धिमत्ता प्रदान करता है। यह चिंतनशील है, भविष्यसूचक नहीं — दर्पण है, निर्णय नहीं।",
    "res.confidence.section": "यह पठन कितना विश्वसनीय है",
    "res.confidence.title": "विश्वास संकेतक",
  },
};

/* ---------------- main page ---------------- */
export default function Results() {
  const { id } = useParams();
  const { lang, t: tGlobal } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fired = useRef(false);

  // Local `t` merges the global dictionary with page-only keys defined above.
  const t = (key, params) => {
    const local = (R_STR[lang] && R_STR[lang][key]) || R_STR.en[key];
    if (local != null) {
      let s = local;
      if (params) for (const [k, v] of Object.entries(params)) s = s.replaceAll(`{${k}}`, String(v));
      return s;
    }
    return tGlobal(key, params);
  };

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    (async () => {
      try {
        const res = await apiClient.get(`/consultations/${id}`);
        setData(res.data);
        track("clarity_card_generated", { consultation_id: id });
      } catch (_) {
        toast.error(t("res.loadError"));
        navigate("/");
      } finally {
        setLoading(false);
      }
    })();
     
  }, [id, navigate]);

  if (loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400">
        {t("res.loading")}
      </div>
    );
  }

  const r = data.reading || {};
  const name = (data.birth?.full_name || "").trim().split(/\s+/)[0] || "Seeker";
  const spiritual = r.spiritual_practice || {};
  const confidence = r.confidence || {};
  const numerology = r.numerology || data.numerology || {};
  const profileDims = r.profile_dimensions || data.profile?.dimensions || {};
  const profileLabels = data.profile?.labels || {};
  const archetype = r.archetype || {};
  const oneLine = r.one_line_answer || r.summary || "";

  const oneLineAnswer = oneLine;
  const finalReflection = r.final_reflection || r.summary || "";

  /* ---------------- Spiritual mantra (Sanskrit + transliteration) ------- */
  const mantra = spiritual.mantra || {};
  const mantraSanskrit = typeof mantra === "object" ? mantra.sanskrit : (typeof mantra === "string" ? mantra : "");
  const mantraTranslit = typeof mantra === "object" ? mantra.transliteration : "";

  return (
    <div className="min-h-screen cosmic-bg">
      <Sparks />

      <header className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2"
          data-testid="results-home-link"
        >
          <Sparkles className="w-5 h-5 gold-text" strokeWidth={1.5} />
          <span className="font-serif text-xl text-white">
            {lang === "hi" ? (
              <>मंत्रणा<span className="gold-text"> One</span></>
            ) : (
              <>Mantrana<span className="gold-text"> One</span></>
            )}
          </span>
        </button>
      </header>

      <main
        className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16"
        data-testid="results-page"
      >
        {/* ─── HERO ─── */}
        <div className="text-xs tracking-[0.25em] uppercase gold-text mb-4">
          {t("res.hero.eyebrow")}
        </div>
        <h1 className="font-serif text-4xl md:text-6xl font-light text-white leading-tight">
          {t("res.hero.greeting", { name })}
        </h1>
        <p className="mt-4 report-body max-w-2xl">
          {t("res.hero.subline")}
        </p>

        {/* Archetype badge (right under the H1) */}
        {archetype.name && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-8 inline-flex flex-col md:flex-row md:items-center gap-3 md:gap-5 rounded-3xl px-6 py-4 gold-glow"
            style={{
              background: "linear-gradient(140deg, rgba(212,175,55,0.14), rgba(49,46,129,0.22))",
              border: "1px solid rgba(212,175,55,0.5)",
            }}
            data-testid="results-archetype"
          >
            <div className="flex items-center gap-2.5">
              <Compass className="w-4 h-4 gold-text" strokeWidth={1.5} />
              <span className="text-[10px] tracking-[0.3em] uppercase gold-text">
                {t("res.archetype")}
              </span>
            </div>
            <div className="flex items-baseline gap-3">
              <span className="font-serif italic text-2xl md:text-3xl text-white leading-none">
                {archetype.name}
              </span>
              {archetype.meaning && (
                <span className="text-slate-300 report-body text-sm md:text-base">
                  — {archetype.meaning}
                </span>
              )}
            </div>
          </motion.div>
        )}

        {/* ─── ONE-LINE ANSWER (big premium card) ─── */}
        {oneLineAnswer && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-10 rounded-3xl p-8 md:p-12"
            style={{
              background:
                "linear-gradient(140deg, rgba(212,175,55,0.16) 0%, rgba(49,46,129,0.28) 100%)",
              border: "1px solid rgba(212,175,55,0.45)",
              boxShadow: "0 30px 80px rgba(0,0,0,0.5), 0 0 60px rgba(212,175,55,0.18)",
            }}
            data-testid="results-one-line-card"
          >
            <div className="text-[10px] tracking-[0.32em] uppercase gold-text mb-4">
              {t("res.oneLine")}:
            </div>
            <p className="font-serif italic text-2xl md:text-4xl text-white leading-snug tracking-tight">
              {`"${oneLineAnswer}"`}
            </p>
          </motion.div>
        )}

        {/* ─── REAL QUESTION (only if the seeker shared personal context) ─── */}
        {r.real_question && r.real_question.provided && (r.real_question.summary || r.real_question.insight) && (
          <Section
            kicker={t("res.realQuestion")}
            testId="results-real-question-section"
          >
            <div
              className="rounded-3xl p-8 md:p-10"
              style={{
                background:
                  "linear-gradient(140deg, rgba(49,46,129,0.28), rgba(11,16,32,0.6))",
                border: "1px solid rgba(212,175,55,0.28)",
              }}
              data-testid="results-real-question"
            >
              {r.real_question.summary && (
                <p className="font-serif italic text-xl md:text-2xl font-light text-white leading-snug mb-6">
                  {`"${r.real_question.summary}"`}
                </p>
              )}
              <div className="space-y-4">
                {r.real_question.emotional_conflict && (
                  <div className="flex flex-col sm:flex-row sm:gap-6">
                    <div className="text-[10px] tracking-[0.22em] uppercase gold-text sm:w-44 mb-1 sm:mb-0 sm:pt-1 shrink-0">
                      {t("res.emotionalConflict")}
                    </div>
                    <p className="report-body flex-1">{r.real_question.emotional_conflict}</p>
                  </div>
                )}
                {r.real_question.hidden_fear && (
                  <div className="flex flex-col sm:flex-row sm:gap-6">
                    <div className="text-[10px] tracking-[0.22em] uppercase gold-text sm:w-44 mb-1 sm:mb-0 sm:pt-1 shrink-0">
                      {t("res.hiddenFear")}
                    </div>
                    <p className="report-body flex-1">{r.real_question.hidden_fear}</p>
                  </div>
                )}
                {r.real_question.decision_tension && (
                  <div className="flex flex-col sm:flex-row sm:gap-6">
                    <div className="text-[10px] tracking-[0.22em] uppercase gold-text sm:w-44 mb-1 sm:mb-0 sm:pt-1 shrink-0">
                      {t("res.decisionTension")}
                    </div>
                    <p className="report-body flex-1">{r.real_question.decision_tension}</p>
                  </div>
                )}
              </div>
              {r.real_question.insight && (
                <div
                  className="mt-6 pt-6 border-t"
                  style={{ borderColor: "rgba(212,175,55,0.25)" }}
                >
                  <p className="font-serif italic text-lg md:text-xl text-white/95 leading-snug">
                    {r.real_question.insight}
                  </p>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* ─── Radar (5-dimension psychological footprint) ─── */}
        {Object.keys(profileDims).length > 0 && (
          <Section
            kicker={t("res.profileTitle")}
            testId="results-profile-section"
          >
            <div className="glass rounded-3xl p-6 md:p-8 grid grid-cols-1 lg:grid-cols-5 gap-6 items-center">
              <div className="lg:col-span-3 flex justify-center">
                <div style={{ maxWidth: 480, width: "100%" }}>
                  <PsychRadar
                    dimensions={profileDims}
                    labels={profileLabels}
                    axisNames={{
                      decision_confidence: t("radar.decision_confidence"),
                      emotional_clarity:   t("radar.emotional_clarity"),
                      risk_readiness:      t("radar.risk_readiness"),
                      purpose_alignment:   t("radar.purpose_alignment"),
                      self_trust:          t("radar.self_trust"),
                    }}
                    size={420}
                  />
                </div>
              </div>
              <div className="lg:col-span-2">
                <p className="report-body text-sm md:text-base">
                  {t("res.profileSubtitle")}
                </p>
                <ul className="mt-5 space-y-2.5">
                  {Object.entries(profileDims).map(([k, v]) => (
                    <li key={k} className="flex items-baseline justify-between gap-3">
                      <span className="text-[11px] tracking-[0.2em] uppercase text-slate-400">
                        {k.replace(/_/g, " ")}
                      </span>
                      <span className="font-serif text-lg gold-text tabular-nums">
                        {Math.round(v)}<span className="text-slate-500 text-xs ml-0.5">/100</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Section>
        )}

        {/* ─── 1 · MIND ─── */}
        <Section testId="results-mind-section">
          <Tile icon={Brain} label={tGlobal("res.mindSays")} testId="results-mind">
            <TherapyBlock data={r.what_your_mind_says} testId="mind" t={t} />
          </Tile>
        </Section>

        {/* ─── 2 · EMOTIONS ─── */}
        <Section testId="results-emotions-section">
          <Tile icon={Heart} label={tGlobal("res.emotionsSay")} testId="results-emotions">
            <TherapyBlock data={r.what_your_emotions_say} testId="emotions" t={t} />
          </Tile>
        </Section>

        {/* ─── 3 · ASTROLOGY ─── */}
        <Section testId="results-astrology-section">
          <Tile icon={Moon} label={tGlobal("res.astrologySuggests")} testId="results-astrology">
            <AstroBlock data={r.what_your_astrology_suggests} testId="astrology" t={t} />
          </Tile>
        </Section>

        {/* ─── 4 · NUMEROLOGY ─── */}
        <Section testId="results-numerology-section">
          <Tile icon={Hash} label={tGlobal("res.numerologySuggests")} testId="results-numerology">
            <NumerologyGrid
              numerology={numerology}
              structured={
                typeof r.what_your_numerology_suggests === "object"
                  ? r.what_your_numerology_suggests
                  : null
              }
            />
            {typeof r.what_your_numerology_suggests === "string" && (
              <p className="report-body mt-4">
                {r.what_your_numerology_suggests}
              </p>
            )}
          </Tile>
        </Section>

        {/* ─── 5 · WHERE SYSTEMS AGREE ─── */}
        {r.where_all_systems_agree && (
          <Section testId="results-convergence-section">
            <div
              className="rounded-3xl p-8 md:p-10 gold-glow"
              style={{
                background:
                  "linear-gradient(140deg, rgba(212,175,55,0.12), rgba(49,46,129,0.18))",
                border: "1px solid rgba(212,175,55,0.4)",
              }}
              data-testid="results-convergence"
            >
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="w-4 h-4 gold-text" strokeWidth={1.5} />
                <div className="text-[10px] tracking-[0.25em] uppercase gold-text">
                  {tGlobal("res.agreement")}
                </div>
              </div>
              <p className="font-serif text-xl md:text-2xl font-light text-white leading-snug">
                {r.where_all_systems_agree}
              </p>
            </div>
          </Section>
        )}

        {/* ─── 6 + 7 · IF WAIT / IF ACT ─── */}
        <Section testId="results-wait-act-section">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Tile icon={PauseCircle} label={tGlobal("res.ifWait")} testId="results-if-wait">
              <p className="report-body">{r.if_you_wait}</p>
            </Tile>
            <div
              className="rounded-3xl p-7 md:p-8 gold-glow"
              style={{
                background:
                  "linear-gradient(140deg, rgba(212,175,55,0.10), rgba(49,46,129,0.18))",
                border: "1px solid rgba(212,175,55,0.35)",
              }}
              data-testid="results-if-act"
            >
              <div className="flex items-center gap-2 mb-4">
                <ArrowUpRight className="w-4 h-4 gold-text" strokeWidth={1.5} />
                <div className="text-[10px] tracking-[0.25em] uppercase gold-text">
                  {tGlobal("res.ifAct")}
                </div>
              </div>
              <p className="report-body text-white">{r.if_you_act}</p>
            </div>
          </div>
        </Section>

        {/* ─── 8 · BIGGEST BLIND SPOT (highlighted warning card) ─── */}
        {r.biggest_blind_spot && (
          <Section testId="results-blind-section">
            <BlindSpotCard data={r.biggest_blind_spot} t={t} />
          </Section>
        )}

        {/* ─── 9 · RECOMMENDED ACTION ─── */}
        {r.recommended_action && (
          <Section testId="results-action-section">
            <Tile icon={Target} label={tGlobal("res.action")} testId="results-action">
              <p className="font-serif text-xl md:text-2xl font-light text-white leading-snug">
                {r.recommended_action}
              </p>
            </Tile>
          </Section>
        )}

        {/* ─── 10 · SPIRITUAL PRACTICE ─── */}
        <Section
          kicker={t("res.spiritual.section")}
          title={t("res.spiritual.title")}
          testId="results-spiritual-section"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Tile icon={Flame} label={tGlobal("res.spiritual.mantra")} testId="spiritual-mantra">
              {mantraSanskrit && (
                <p className="font-serif text-2xl text-white leading-snug" lang="sa">
                  {mantraSanskrit}
                </p>
              )}
              {mantraTranslit && (
                <p className="mt-2 text-sm text-slate-300 tracking-wide italic">
                  {mantraTranslit}
                </p>
              )}
            </Tile>
            <Tile icon={MapPin} label={tGlobal("res.spiritual.temple")} testId="spiritual-temple">
              <p className="report-body">{spiritual.temple}</p>
            </Tile>
            <Tile icon={Sparkles} label={tGlobal("res.spiritual.meditation")} testId="spiritual-meditation">
              <p className="report-body">{spiritual.meditation}</p>
            </Tile>
            <Tile icon={BookHeart} label={tGlobal("res.spiritual.reflection")} testId="spiritual-reflection">
              <p className="report-body italic">{spiritual.reflection}</p>
            </Tile>
          </div>
          {spiritual.why_this_practice && (
            <div
              className="mt-6 rounded-3xl p-6 md:p-7"
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(212,175,55,0.22)",
              }}
              data-testid="spiritual-why"
            >
              <div className="text-[10px] tracking-[0.25em] uppercase gold-text mb-2">
                {tGlobal("res.spiritual.why")}
              </div>
              <p className="report-body">{spiritual.why_this_practice}</p>
            </div>
          )}
        </Section>

        {/* ─── CONFIDENCE (numeric %) ─── */}
        <Section
          kicker={t("res.confidence.section")}
          title={t("res.confidence.title")}
          testId="results-confidence-section"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <ConfidenceBar label={t("res.psychConfidence")} value={confidence.psychological ?? confidence.behavioral} />
            <ConfidenceBar label={t("res.astroAlignment")} value={confidence.astrological} />
            <ConfidenceBar label={t("res.numConsistency")} value={confidence.numerological} />
            <ConfidenceBar label={t("res.overallClarity")} value={confidence.overall_clarity} />
          </div>
        </Section>

        {/* ─── 12 · FINAL REFLECTION ─── */}
        {finalReflection && (
          <Section testId="results-reflection-section">
            <div
              className="glass rounded-3xl p-10 md:p-12 gold-glow"
              data-testid="results-reflection"
            >
              <div className="flex items-center gap-2 mb-5">
                <Quote className="w-4 h-4 gold-text" strokeWidth={1.5} />
                <div className="text-[10px] tracking-[0.25em] uppercase gold-text">
                  {t("res.finalReflection")}
                </div>
              </div>
              <p className="font-serif text-2xl md:text-3xl font-light text-white leading-snug">
                {finalReflection}
              </p>
            </div>
          </Section>
        )}

        {/* Share card */}
        <ClarityCard reading={r} name={name} consultationId={data.id} />

        {/* CTA */}
        <div className="mt-14 flex flex-wrap gap-4">
          <button
            onClick={() => navigate("/onboarding")}
            className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-medium gold-glow"
            style={{ background: "#D4AF37", color: "#0B1020" }}
            data-testid="results-new-reading-button"
          >
            {t("res.newReading")} <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <p className="mt-10 text-[11px] text-slate-500 font-light italic max-w-2xl">
          {t("res.disclaimer")}
        </p>
      </main>
    </div>
  );
}
